import { useInfiniteQuery } from '@tanstack/react-query';
import { getReleaseNotes } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';

interface IUseReleaseNotesParams {
  productIdentifier: string;
  language?: string;
  relations?: string[];
  extraFilters?: Record<string, string | number | undefined>;
  limit?: number;
  pageSize?: number;
  search?: string;
  fields?: string[];
  latestOnly?: boolean;
}

export const useGetReleaseNotes = (params: IUseReleaseNotesParams) => {
  const fetchReleaseNotes = async ({ pageParam = 1 }) => {
    const { relations, extraFilters, ...rest } = params;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    for (const [key, value] of Object.entries(extraFilters ?? {})) {
      queryString.append(`filter.${key}`, String(value));
    }

    for (const rel of relations ?? []) {
      queryString.append('relations', rel);
    }

    queryString.append('page', String(pageParam));
    try {
      return await getReleaseNotes(`${API_URLS.RELEASE_NOTES}?${queryString.toString()}`);
    } catch (error) {
      console.error('Error fetching release notes:', error);
      throw new Error((error as { message: string }).message || 'Failed to fetch release notes');
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['release-notes-list', params],
    queryFn: fetchReleaseNotes,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedCount = allPages.flatMap((page) => page.data).length;
      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.items) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  };
};
